import { useDndContext, useDroppable } from '@dnd-kit/core';
import { Breadcrumb } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
    canDropOnParent,
    crumbDroppableId,
    itemFromDraggable,
    type FileDropData,
} from './model/fileDropRules';
import { breadcrumbSegments } from './model/folderTree';

interface FolderBreadcrumbsProps {
    currentPath: string;
    onNavigate: (path: string) => void;
    droppable?: boolean;
}

interface DroppableCrumbProps {
    path: string;
    label: string;
    isCurrent: boolean;
    droppable?: boolean;
    onNavigate: (path: string) => void;
}

const DroppableCrumb = ({ path, label, isCurrent, droppable, onNavigate }: DroppableCrumbProps) => {
    const canDrop = Boolean(droppable) && !isCurrent;
    const { setNodeRef, isOver } = useDroppable({
        id: crumbDroppableId(path),
        data: { type: 'crumb', parentPath: path } satisfies FileDropData,
        disabled: !canDrop,
    });
    const { active } = useDndContext();
    const dropOk = Boolean(canDrop && isOver && canDropOnParent(itemFromDraggable(active?.data.current), path));

    return (
        <Breadcrumb.Item
            active={isCurrent}
            href="#"
            onClick={(event) => {
                event.preventDefault();
                onNavigate(path);
            }}
            className={['text-truncate no-select', dropOk ? 'file-crumb-item--drop-target' : '']
                .filter(Boolean)
                .join(' ')}
        >
            <span ref={setNodeRef} className={dropOk ? 'file-crumb--drop-target text-white fw-medium' : undefined}>
                {label}
            </span>
        </Breadcrumb.Item>
    );
};

const FolderBreadcrumbs = ({ currentPath, onNavigate, droppable }: FolderBreadcrumbsProps) => {
    const { t } = useTranslation(['FileNavigator']);
    const segments = breadcrumbSegments(currentPath);

    return (
        <Breadcrumb className="mb-2">
            <DroppableCrumb
                path=""
                label={t('rootFolder')}
                isCurrent={currentPath === ''}
                droppable={droppable}
                onNavigate={onNavigate}
            />
            {segments.map((segment) => (
                <DroppableCrumb
                    key={segment.path}
                    path={segment.path}
                    label={segment.label}
                    isCurrent={segment.path === currentPath}
                    droppable={droppable}
                    onNavigate={onNavigate}
                />
            ))}
        </Breadcrumb>
    );
};

export default FolderBreadcrumbs;
